// Root-level handler for errors caught by React error boundaries, wired into
// `createRoot(..., { onCaughtError })` in src/main.tsx. Extracted so the logic
// is unit-testable without bootstrapping the full app (mirrors stale-chunk-reload.ts).

import { isPdfWorkerTeardownError } from '@/components/shared/pdf/pdf-worker-teardown';
import { STALE_CHUNK_RELOAD_STALL_MS, startStaleChunkRecovery } from '@/lib/stale-chunk-reload';

interface CaughtErrorInfo {
  readonly componentStack?: string | null;
}

/**
 * React 19's default `onCaughtError` logs EVERY boundary-caught error via
 * `console.error` — including the benign PDF.js worker-teardown race that
 * `PdfWorkerResetBoundary` catches and self-heals (see
 * pdf-worker-reset-boundary.tsx). Sentry captures `console.error`, so the
 * default turns each self-healed recovery into console + Sentry noise.
 *
 * This handler suppresses the known teardown signatures and preserves the
 * default logging for everything else. An unrecoverable PDF fault is still
 * surfaced: the boundary logs its own `'PDF render failed'` line when its
 * reset budget is exhausted, and that message is not a teardown signature.
 *
 * A stale deploy chunk is the same shape: the recovery reload replaces the page
 * and the next boot reports it. A throttled reload, or one the user cancels, leaves
 * the page broken, so those are logged: at once, or when the reload is overdue.
 */
export function handleCaughtError(error: unknown, errorInfo: CaughtErrorInfo): void {
  if (isPdfWorkerTeardownError(error)) {
    return;
  }
  if (startStaleChunkRecovery(error)) {
    setTimeout(() => {
      console.error(error, errorInfo.componentStack);
    }, STALE_CHUNK_RELOAD_STALL_MS);
    return;
  }
  console.error(error, errorInfo.componentStack);
}
